/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { MDSButton } from '@hashicorp/mds-react'
import {
	convertTimeToLocalOffset,
	convertTimeToShortDate,
} from 'components/_page-components/events/event-data-utils/datetime-utils'
import Live from 'components/_page-components/events/webinar-states/live'
import Recorded from 'components/_page-components/events/webinar-states/recorded'
import s from '../../webinar.module.css'

function hasWebinarStarted(startTime, minutesBeforeStartTime = 15) {
	const currentTime = Date.now()
	const webinarStart = new Date(startTime).getTime()
	const comparisonTime = minutesBeforeStartTime
		? webinarStart - minutesBeforeStartTime * 60
		: webinarStart
	return currentTime > comparisonTime
}

function BackButton({ text, url }) {
	return (
		<MDSButton
			className={s.backButton}
			type="button"
			title={text}
			url={url}
			theme={{ variant: 'tertiary-neutral' }}
			icon="arrow-left"
			iconPosition="leading"
		/>
	)
}

export function RenderWebinarState({ event, marketoFormId }) {
	const webinarHasStarted = hasWebinarStarted(event.startTime)
	const recorded = webinarHasStarted && event.videoUrl
	const live = !webinarHasStarted
	const pending = webinarHasStarted && !recorded

	// Aug 20 2020
	const date = convertTimeToShortDate(event.startTime)
	// 2:00 PM PST (or PDT) - this is taking place in browser and is wise to Daylight vs. Standard
	const time = convertTimeToLocalOffset(event.startTime)
	const tagLine = `${date}  |  ${time} ${
		event.duration ? `  |  ${event.duration}` : ''
	}`

	const Back = () => (
		<BackButton
			{...(live
				? { text: 'Back to all events', url: '/events' }
				: {
						text: 'Back to all recorded webinars',
						url: '/events/webinars/recorded',
					})}
		/>
	)

	if (recorded) {
		return (
			<Recorded tagLine={tagLine} {...event} marketoFormId={marketoFormId} />
		)
	}

	if (live) {
		return (
			<>
				<Back />
				<Live
					tagLine={tagLine}
					{...event}
					pending={false}
					marketoFormId={marketoFormId}
				/>
			</>
		)
	}

	if (pending) {
		return (
			<>
				<Back />
				<Live
					tagLine={tagLine}
					{...event}
					pending
					marketoFormId={marketoFormId}
				/>
			</>
		)
	}

	return null
}
